import { accountsWidgetDefinition } from './widgets/accounts';
import { alertsWidgetDefinition } from './widgets/alerts';
import { backtestWidgetDefinition } from './widgets/backtest';
import { basketTradingWidgetDefinition } from './widgets/basket-trading';
import { eventsWidgetDefinition } from './widgets/events';
import { executionsWidgetDefinition } from './widgets/executions';
import { filingsWidgetDefinition } from './widgets/filings';
import { locatesWidgetDefinition } from './widgets/locates';
import { locatesV2WidgetDefinition } from './widgets/locates-v2';
import { manualReviewWidgetDefinition } from './widgets/manual-review';
import { messagesWidgetDefinition } from './widgets/messages';
import { newsWidgetDefinition } from './widgets/news';
import { orderBlotterWidgetDefinition } from './widgets/order-blotter';
import { ordersWidgetDefinition } from './widgets/orders';
import { positionsWidgetDefinition } from './widgets/positions';
import { resultsWidgetDefinition } from './widgets/results';
import { scannerWidgetDefinition } from './widgets/scanner';
import { screenerWidgetDefinition } from './widgets/screener';
import { searchAlphaWidgetDefinition } from './widgets/searchalpha';
import { tableWidgetDefinition } from './widgets/table';
import { tradeProWidgetDefinition } from './widgets/trade-pro';
import { tradeProMultiWidgetDefinition } from './widgets/trade-pro-multi';
import { tradingViewChartWidgetDefinition } from './widgets/tradingview-chart';
import { watchlistWidgetDefinition } from './widgets/watchlist';
import { generatorRoutine } from '@thinkalpha/ui-core/routine/index';
import type { GeneratorContext } from '@thinkalpha/ui-core/routine/index';
import type { TradeProWidgetModel, Widget } from '@thinkalpha/ui-types/contracts/workspace';
import { assertNever, UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { normalizeLegacyAlphaEyeWidget } from 'src/lib/widget/alphaEyeCompatibility';
import type { LegacyWidgetViewModel, SelfOwnedWidgetViewModel } from 'src/store/types';

export type LegacyContractWidget = Exclude<Widget, { type: SelfOwnedWidgetViewModel['type'] }>;

export * from './createTabFromViewModel';
export * from './createWorkspaceFromViewModels';
export * from './generateWidgetTabViewModels';

export const widgetToViewModel = generatorRoutine(function* (ctx: GeneratorContext, widget: LegacyContractWidget) {
    switch (widget.type) {
        case 'accounts':
            return accountsWidgetDefinition.toViewModel(widget);
        case 'alerts':
            return alertsWidgetDefinition.toViewModel(widget);
        case 'backtest':
            return backtestWidgetDefinition.toViewModel(widget);
        case 'alpha-lens':
            return normalizeLegacyAlphaEyeWidget(widget);
        case 'events':
            return eventsWidgetDefinition.toViewModel(widget);
        case 'executions':
            return executionsWidgetDefinition.toViewModel(widget);
        case 'filings':
            return filingsWidgetDefinition.toViewModel(widget);
        case 'locates':
            return locatesWidgetDefinition.toViewModel(widget);
        case 'lab':
            return locatesV2WidgetDefinition.toViewModel(widget);
        case 'messages':
            return messagesWidgetDefinition.toViewModel(widget);
        case 'news':
            return newsWidgetDefinition.toViewModel(widget);
        case 'tradingview-chart':
            return tradingViewChartWidgetDefinition.toViewModel(widget);
        case 'order-blotter':
            return orderBlotterWidgetDefinition.toViewModel(widget);
        case 'trade-pro':
            return tradeProWidgetDefinition.toViewModel(widget);
        case 'trade-pro-multi':
            return tradeProMultiWidgetDefinition.toViewModel(widget);
        case 'order-entry' as any:
            // 'order-entry' is a legacy type, but we use it to load the workspace, hence the cast
            return tradeProWidgetDefinition.toViewModel(widget as TradeProWidgetModel);
        case 'orders':
            return ordersWidgetDefinition.toViewModel(widget);
        case 'manual-review':
            return manualReviewWidgetDefinition.toViewModel(widget);
        case 'positions':
            return positionsWidgetDefinition.toViewModel(widget);
        case 'results':
            return resultsWidgetDefinition.toViewModel(widget);
        case 'scanner':
            return yield ctx.call(scannerWidgetDefinition.toViewModel, widget);
        case 'screener':
            return yield ctx.call(screenerWidgetDefinition.toViewModel, widget);
        case 'searchalpha':
            return yield ctx.call(searchAlphaWidgetDefinition.toViewModel, widget);
        case 'basket-trading':
            return basketTradingWidgetDefinition.toViewModel(widget);
        case 'table':
            return tableWidgetDefinition.toViewModel(widget);
        case 'watchlist':
            return yield ctx.call(watchlistWidgetDefinition.toViewModel, widget);
        default:
            return assertNever(widget, null);
    }
});

export function widgetToContractModel(widget: LegacyWidgetViewModel): Widget {
    switch (widget.type) {
        case 'accounts':
            return accountsWidgetDefinition.toContractModel(widget);
        case 'alerts':
            return alertsWidgetDefinition.toContractModel(widget);
        case 'backtest':
            return backtestWidgetDefinition.toContractModel(widget);
        case 'alpha-lens':
            return normalizeLegacyAlphaEyeWidget(widget);
        case 'events':
            return eventsWidgetDefinition.toContractModel(widget);
        case 'executions':
            return executionsWidgetDefinition.toContractModel(widget);
        case 'filings':
            return filingsWidgetDefinition.toContractModel(widget);
        case 'locates':
            return locatesWidgetDefinition.toContractModel(widget);
        case 'lab':
            return locatesV2WidgetDefinition.toContractModel(widget);
        case 'messages':
            return messagesWidgetDefinition.toContractModel(widget);
        case 'news':
            return newsWidgetDefinition.toContractModel(widget);
        case 'tradingview-chart':
            return tradingViewChartWidgetDefinition.toContractModel(widget);
        case 'order-blotter':
            return orderBlotterWidgetDefinition.toContractModel(widget);
        case 'trade-pro':
            return tradeProWidgetDefinition.toContractModel(widget);
        case 'trade-pro-multi':
            return tradeProMultiWidgetDefinition.toContractModel(widget);
        case 'orders':
            return ordersWidgetDefinition.toContractModel(widget);
        case 'manual-review':
            return manualReviewWidgetDefinition.toContractModel(widget);
        case 'positions':
            return positionsWidgetDefinition.toContractModel(widget);
        case 'results':
            return resultsWidgetDefinition.toContractModel(widget);
        case 'scanner':
            return scannerWidgetDefinition.toContractModel(widget);
        case 'screener':
            return screenerWidgetDefinition.toContractModel(widget);
        case 'searchalpha':
            return searchAlphaWidgetDefinition.toContractModel(widget);
        case 'basket-trading':
            return basketTradingWidgetDefinition.toContractModel(widget);
        case 'table':
            return tableWidgetDefinition.toContractModel(widget);
        case 'watchlist':
            return watchlistWidgetDefinition.toContractModel(widget);
        default:
            throw new UnreachableCaseError(widget);
    }
}
